import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import {
    Heart, Sparkles, Loader2, Check, Bookmark, Phone, ArrowRight,
    TrendingDown, TrendingUp, Minus, Flame,
} from "lucide-react";

const DOMAIN_LABELS = {
    stress: "Stress", sleep: "Sleep", mood: "Mood", energy: "Energy",
    support: "Support", confidence: "Confidence", self_care: "Time for you",
};

/** Daily check-in — 1-2 rotating questions, under 30 seconds. */
export function DailyWellnessCheckIn({ onDone }) {
    const sid = getSessionId();
    const [today, setToday] = useState(null);
    const [answers, setAnswers] = useState({});
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        let on = true;
        api.get(`/wellness-hub/${sid}/today`).then(r => {
            if (!on) return;
            setToday(r.data);
            setDone(!!r.data.already_answered);
        }).catch(() => on && setToday({ questions: [], free_text: null }));
        return () => { on = false; };
    }, [sid]);

    const submit = async () => {
        setSaving(true);
        try {
            await api.post("/wellness-hub/check-in", { session_id: sid, answers, note });
            setDone(true);
            toast.success("Thanks for checking in on yourself.");
            onDone && onDone();
        } catch {
            toast.error("Couldn't save. Try again.");
        } finally { setSaving(false); }
    };

    if (!today) return null;
    if (done) {
        return (
            <div className="ml-card p-6 bg-gradient-to-br from-teal-soft/50 to-white" data-testid="daily-checkin-done">
                <p className="text-sm font-semibold text-teal-dark flex items-center gap-2">
                    <Check size={16} /> You checked in today
                </p>
                <p className="text-sm text-[#4C5551] mt-1">That small habit is how Lane learns to look out for you. See you tomorrow.</p>
            </div>
        );
    }

    const questions = today.questions || [];
    return (
        <div className="ml-card p-6 bg-gradient-to-br from-teal-soft/50 to-white" data-testid="daily-checkin-card">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-dark flex items-center gap-1.5">
                <Heart size={13} strokeWidth={2} /> Today's check-in · under 30 seconds
            </p>
            <div className="mt-4 space-y-5">
                {questions.map(q => (
                    <div key={q.id} data-testid={`checkin-q-${q.id}`}>
                        <p className="text-base font-medium text-[#1F2624]">{q.text}</p>
                        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                            {Array.from({ length: 11 }, (_, i) => (
                                <button key={i} type="button" data-testid={`checkin-${q.id}-${i}`}
                                    onClick={() => setAnswers(a => ({ ...a, [q.domain]: i }))}
                                    className={`w-11 h-11 rounded-full text-sm font-semibold transition-all ${answers[q.domain] === i ? "bg-teal text-white scale-110" : "bg-white border border-cream-dark text-[#4C5551] hover:border-teal"}`}>
                                    {i}
                                </button>
                            ))}
                        </div>
                        <div className="mt-1 flex justify-between text-[11px] text-[#6E7A74] max-w-[352px]">
                            <span>{q.low}</span><span>{q.high}</span>
                        </div>
                    </div>
                ))}
                {today.free_text && (
                    <div>
                        <p className="text-sm text-[#4C5551]">{today.free_text.text} <span className="text-[#6E7A74]">(optional)</span></p>
                        <input className="ml-input mt-1.5 !py-2 text-sm" value={note} onChange={e => setNote(e.target.value)}
                            placeholder="A sentence is plenty…" data-testid="checkin-note" />
                    </div>
                )}
                <button type="button" onClick={submit} disabled={saving || Object.keys(answers).length === 0}
                    className="ml-btn-primary text-sm disabled:opacity-50" data-testid="checkin-submit">
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Done
                </button>
            </div>
        </div>
    );
}

/** Wellness dashboard — domain cards + coach, no scores or clinical labels. */
export function WellnessTrendsAndCoach({ refreshKey }) {
    const sid = getSessionId();
    const [data, setData] = useState(null);

    useEffect(() => {
        let on = true;
        api.get(`/wellness-hub/${sid}/summary`).then(r => on && setData(r.data)).catch(() => on && setData({}));
        return () => { on = false; };
    }, [sid, refreshKey]);

    if (!data) return null;
    const trends = data.trends || {};
    const withData = Object.entries(trends).filter(([, t]) => t.avg_load !== null && t.avg_load !== undefined);

    const moodWord = (load) => load >= 7 ? "Heavy" : load >= 4.5 ? "Carrying a lot" : "Steady";
    const DirIcon = (dir) => dir === "easier" ? TrendingDown : dir === "heavier" ? TrendingUp : Minus;

    return (
        <div className="space-y-4" data-testid="wellness-trends">
            {data.coach && (
                <div className="ml-card p-5 bg-white border-l-4 border-l-teal" data-testid="wellness-coach">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-dark flex items-center gap-1.5">
                        <Sparkles size={13} /> From Lane
                    </p>
                    <p className="mt-1.5 text-sm text-[#1F2624] leading-relaxed">{data.coach}</p>
                    <Link to="/chat" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-dark font-medium hover:underline min-h-[44px]">
                        Talk it through with Lane <ArrowRight size={13} />
                    </Link>
                </div>
            )}
            {withData.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {withData.map(([d, t]) => {
                        const Icon = DirIcon(t.direction);
                        return (
                            <div key={d} className="ml-card p-4 bg-white" data-testid={`wellness-domain-${d}`}>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">{DOMAIN_LABELS[d] || d}</p>
                                <p className="mt-1 text-base font-semibold text-[#1F2624]">{moodWord(t.avg_load)}</p>
                                {t.direction && (
                                    <p className={`mt-0.5 text-xs inline-flex items-center gap-1 ${t.direction === "easier" ? "text-teal-dark" : t.direction === "heavier" ? "text-coral-dark" : "text-[#6E7A74]"}`}>
                                        <Icon size={12} /> {t.direction === "easier" ? "Getting lighter" : t.direction === "heavier" ? "Heavier this week" : "About the same"}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {(data.streak_days || 0) >= 2 && (
                <p className="text-xs text-[#6E7A74] inline-flex items-center gap-1.5" data-testid="wellness-streak">
                    <Flame size={13} className="text-coral-dark" /> {data.streak_days}-day check-in streak — you're showing up for yourself too.
                </p>
            )}
        </div>
    );
}

/** Care For You — personalized caregiver resource center. */
export function CareForYou() {
    const sid = getSessionId();
    const [data, setData] = useState(null);

    const load = () => api.get(`/wellness-hub/${sid}/care-for-you`).then(r => setData(r.data)).catch(() => setData({ items: [] }));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);

    const toggleSave = async (key) => {
        try {
            const r = await api.post(`/wellness-hub/${sid}/save-resource`, { category: key });
            setData(d => ({ ...d, items: d.items.map(it => it.key === key ? { ...it, saved: r.data.saved } : it) }));
            toast.success(r.data.saved ? "Saved to your wellness list." : "Removed from saved.");
        } catch { toast.error("Couldn't save."); }
    };

    if (!data) return null;
    const items = data.items || [];
    const recommended = items.filter(i => i.recommended);
    const rest = items.filter(i => !i.recommended);

    const Card = ({ it }) => (
        <div className={`ml-card p-4 bg-white flex flex-col ${it.recommended ? "border-teal/40" : ""}`} data-testid={`care-for-you-${it.key}`}>
            <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-[#1F2624]">{it.title}</p>
                <button type="button" onClick={() => toggleSave(it.key)} data-testid={`care-for-you-save-${it.key}`}
                    className={`shrink-0 -m-2 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center ${it.saved ? "text-teal-dark" : "text-[#6E7A74] hover:text-teal-dark"}`} title={it.saved ? "Saved" : "Save"}>
                    <Bookmark size={16} fill={it.saved ? "currentColor" : "none"} />
                </button>
            </div>
            {it.why && <p className="mt-1 text-xs text-[#4C5551] leading-snug">{it.why}</p>}
            <div className="mt-auto pt-2.5 flex items-center gap-3 text-xs">
                <Link to={`/help-finder?category=${encodeURIComponent(it.partner_category)}`} className="text-teal-dark font-medium hover:underline inline-flex items-center gap-1 min-h-[44px]">
                    {it.local_partners > 0 ? `${it.local_partners} trusted partner${it.local_partners > 1 ? "s" : ""}` : "Find nearby"} <ArrowRight size={12} />
                </Link>
            </div>
        </div>
    );

    return (
        <section className="space-y-4" data-testid="care-for-you">
            <div>
                <h2 className="text-xl font-heading font-semibold text-[#1F2624]">Care for you</h2>
                <p className="text-sm text-[#4C5551] mt-0.5">
                    {data.personalized
                        ? "Based on how the last couple of weeks have felt, a few things worth considering — for you, not for them."
                        : "Support that's just for you — because the caregiver counts too."}
                </p>
            </div>
            {recommended.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="care-for-you-recommended">
                    {recommended.map(it => <Card key={it.key} it={it} />)}
                </div>
            )}
            <details className="ml-card p-4 bg-white" data-testid="care-for-you-all">
                <summary className="text-sm font-semibold text-[#4C5551] cursor-pointer">
                    {recommended.length ? `Browse everything (${rest.length} more)` : `Browse all categories (${items.length})`}
                </summary>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(recommended.length ? rest : items).map(it => <Card key={it.key} it={it} />)}
                </div>
            </details>
        </section>
    );
}
